import { signInWithEmailAndPassword, signOut } from "firebase/auth"
import { auth } from "@/config/firebaseApp";
import { esCuentaSoloDeMedico, userIsAllowed } from "@/utils/functions";
import { loginUser } from "@/modules/users/infraestructure/ssrUserRequests";
import { removeAuthCookies, setAuthServerToken } from "@/modules/users/service/auth";
import { useMutation } from "@tanstack/react-query";
import { user_url } from "@/config/endpoints";

/**
 * Marcadores de por qué se rechazó el ingreso.
 *
 * Van como constantes y no como texto suelto porque el login los compara para
 * elegir qué mostrar. Antes comparaba contra `"No tienes permisos"`, que **no
 * lo lanzaba nadie**: el rechazo por permisos caía en el mensaje genérico y
 * encima se reportaba como error de sistema.
 */
export const ERROR_SIN_PERMISOS =
  'Tu cuenta no tiene permisos suficientes para entrar al portal'
export const ERROR_CUENTA_DE_MEDICO = 'Esta cuenta es de un médico'


export function useSignIn(){
  return useMutation({
    mutationFn: signIn
  })
}

export const resetUserPassword = async ({ uid, password }: { uid: string; password: string }) => {
  const firebaseToken = await auth.currentUser?.getIdToken(true);
  const res = await fetch(`${user_url}/${uid}/resetPassword`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${firebaseToken}`,
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify({ password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message ?? "No se pudo actualizar la contraseña");
  }
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: resetUserPassword,
  });
}

/**
 * Le crea la cuenta a alguien que está en la ficha pero no puede entrar.
 *
 * Es el caso de la nómina de Trenes: ninguna de esas personas tiene email ni
 * cuenta de Firebase, así que tildarle el rol de supervisor no le da acceso.
 * El backend crea la cuenta reusando el uid que ya tiene, para no partir a la
 * persona en dos registros.
 */
export const grantPortalAccess = async ({
  uid,
  email,
  password,
  role,
}: {
  uid: string;
  email: string;
  password: string;
  role?: number;
}) => {
  const firebaseToken = await auth.currentUser?.getIdToken(true);
  const res = await fetch(`${user_url}/${uid}/access`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${firebaseToken}`,
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify({ email, password, ...(role ? { role } : {}) }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // El mensaje del backend distingue los casos que le importan a quien está
    // en la pantalla (email ya usado, cuenta ya existente), así que se muestra
    // tal cual en vez de uno genérico.
    throw new Error(json.message ?? "No se pudo dar el acceso al portal");
  }
  return json;
};

export function useGrantPortalAccess() {
  return useMutation({
    mutationFn: grantPortalAccess,
  });
}

export const signIn = async ({email, password} : {email:string, password:string}) => {
    const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);

    const idTokenResult = await firebaseUser?.getIdTokenResult();
    const user = await getLoggedUserData(idTokenResult.token);
    if (!user.roles || !userIsAllowed(user.roles)) {
      await signOut(auth)
      await removeAuthCookies()
      throw new Error(ERROR_SIN_PERMISOS)
    }

    /*
     * Una cuenta que es **sólo** de médico no trabaja acá: su lugar es la app
     * del consultorio. Hasta ahora entraba igual, porque `userIsAllowed` deja
     * pasar cualquier rol conocido y `DOCTOR` es uno de ellos.
     *
     * Se rechaza con un marcador propio para que el login pueda ofrecerle el
     * link correcto en vez del "no pudimos iniciar sesión" genérico.
     */
    if (esCuentaSoloDeMedico(user.roles)) {
      await signOut(auth)
      await removeAuthCookies()
      throw new Error(ERROR_CUENTA_DE_MEDICO)
    }

    await setAuthServerToken(idTokenResult.token, firebaseUser.uid,  idTokenResult.expirationTime);

    return { ...firebaseUser, data: user };
}

export const getLoggedUserData = async (token: string) => {
  const user = await loginUser(token);
  return user;
}